import * as React from 'react';
import {Motion, spring} from 'react-motion';

import ResizeObserver from 'resize-observer-polyfill';

import {Button} from '../button/button';
import {colors} from '../colors/colors';
import {Dialog} from '../dialog/dialog';

import {AdderCircle} from './adder-circle';
import {EmptyCircle} from './empty-circle';
import {RemovableEmptyCircle} from './removable-empty-circle';

import styles from './list-sequence.module.css';

// TODO if we generalize this, we'll need to find a better way to set these
const PADDING_TOP = 24;
const PADDING_BOTTOM = 48;
const PADDING_BOTTOM_WITH_ADDER = 22;

type RowItem = {
    icon?: React.ReactElement<any>;
    body: React.ReactElement<any>;
    iconOffset?: number;
};

type Props<T> = {
    collection: T[];
    adder?: {
        text: React.ReactNode;
        onClick: () => void;
        isDisabled?: boolean;
    };
    shouldShowCounts?: boolean;
    renderRow: (arg1: {
        row: T;
        index: number;
        shouldShowCounts: boolean;
        isSelected: boolean;
        onSelect: (selectedRow?: number) => void;
    }) => RowItem;
    selectedRowId?: string;
    onRemove?: (id: string, index: number) => void;
    /* Poor name, but we set this when we want the user to remove all but the first item */
    keepingOnlyFirst?: boolean;
};

type State = {
    selectedRowIndex: number | null | undefined;
    isAdderHovered: boolean;
    isSelectionDotShown: boolean;
    rowToRemove?: {
        index: number;
        id: string;
    };
};

export class ListSequence<
    T extends {
        id: string;
    },
> extends React.PureComponent<Props<T>, State> {
    listItemsDiv: HTMLElement | null | undefined;
    height = 64;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    circles: HTMLElement[] = [];

    constructor(props: Props<T>) {
        super(props);
        this.state = {
            selectedRowIndex: this.findRowIndex(props.collection, props.selectedRowId),
            isAdderHovered: false,
            // Need to default this to false, to keep it from animating in on mount
            isSelectionDotShown: false,
        };
    }

    componentDidMount() {
        const minHeight = PADDING_TOP + PADDING_BOTTOM;
        // We need to make sure that the line on the left stays the correct size,
        // even if the height of the rows on the right is changing.
        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.listItemsDiv) {
                    const cr = entry.contentRect;
                    // Avoid a negative adjusted bar height.
                    this.height = cr.height > minHeight ? cr.height : minHeight;
                    this.forceUpdate();
                }
            }
        });

        if (this.listItemsDiv && this.listItemsDiv instanceof HTMLElement) {
            this.resizeObserver.observe(this.listItemsDiv);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props<T>) {
        if (
            nextProps.selectedRowId !== this.props.selectedRowId ||
            nextProps.collection !== this.props.collection
        ) {
            const selectedRowIndex = this.findRowIndex(
                nextProps.collection,
                nextProps.selectedRowId
            );

            this.setState({
                selectedRowIndex,
                isSelectionDotShown: Boolean(typeof selectedRowIndex === 'number'),
            });
        }
    }

    render() {
        const dotTop = this.getDotTop();
        const {onRemove} = this.props;

        return (
            // It might seem strange to apply the height like this, since we're
            // calculating `this.height` from the contained `ol` element.
            // But without this, Firefox renders this div at the height of our
            // default this.height (64px), and does not repaint until something changes.
            <div style={{height: this.height}} className={styles['list-dot-sequence']}>
                <Bar
                    height={this.height}
                    paddingTop={PADDING_TOP}
                    paddingBottom={this.props.adder ? PADDING_BOTTOM_WITH_ADDER : PADDING_BOTTOM}
                />
                <ol
                    ref={(node) => {
                        this.listItemsDiv = node;
                    }}
                >
                    {this.props.collection.map((row, index) => {
                        const {icon, body, iconOffset} = this.props.renderRow({
                            row,
                            index,
                            shouldShowCounts: Boolean(this.props.shouldShowCounts),
                            isSelected: index === this.state.selectedRowIndex,
                            onSelect: this.handleSelect(index),
                        });

                        const defaultIcon = onRemove ? (
                            <RemovableEmptyCircle
                                offsetTop={iconOffset}
                                forceRemoveIconVisible={
                                    // The reason we have this prop is to make it easy to remove
                                    // followups.  Not to remove the first one.
                                    this.props.keepingOnlyFirst && index !== 0
                                }
                                onClick={() => {
                                    if (this.props.keepingOnlyFirst) {
                                        onRemove(row.id, index);
                                    } else {
                                        this.handleShowRemoveDialog(row.id, index);
                                    }
                                }}
                            />
                        ) : (
                            <EmptyCircle offsetTop={iconOffset} />
                        );

                        return (
                            <li key={row.id}>
                                <div
                                    ref={(node?: HTMLElement | null) => {
                                        if (!node) return;

                                        if (this.circles[index]) {
                                            this.circles[index] = node;
                                        } else {
                                            this.circles.push(node);
                                        }
                                    }}
                                >
                                    {icon || defaultIcon}
                                </div>
                                {body}
                            </li>
                        );
                    })}
                    {this.props.adder ? (
                        <li>
                            <AdderCircle offsetTop={7} isHovered={this.state.isAdderHovered} />
                            <Button
                                variant='text-primary'
                                size='big'
                                onClick={this.props.adder.onClick}
                                onMouseEnter={this.hoverAdder}
                                onMouseLeave={this.unhoverAdder}
                                disabled={this.props.adder.isDisabled}
                            >
                                {this.props.adder.text}
                            </Button>
                        </li>
                    ) : null}
                    {this.state.rowToRemove ? (
                        <Dialog
                            customStyles={{
                                marginTop: this.state.rowToRemove.index === 0 ? '-11px' : '5px',
                            }}
                            location='right'
                            question='Remove?'
                            isWarning={true}
                            primaryText='Yes'
                            onPrimary={this.handleRemove}
                            onSecondary={this.hideRemoveDialog}
                            secondaryText='No'
                            anchor={this.circles[this.state.rowToRemove.index]}
                        />
                    ) : undefined}
                </ol>
                {this.state.isSelectionDotShown ? <SelectionDot top={dotTop} /> : null}
            </div>
        );
    }

    handleShowRemoveDialog = (id: string, index: number) => {
        this.setState({
            rowToRemove: {
                index: index,
                id: id,
            },
        });
    };

    handleRemove = () => {
        if (this.state.rowToRemove && this.props.onRemove) {
            this.props.onRemove(this.state.rowToRemove.id, this.state.rowToRemove.index);
        }

        this.hideRemoveDialog();
    };

    hideRemoveDialog = () => {
        this.setState({
            rowToRemove: undefined,
        });
    };

    // Note: We're not allowing unselecting from this method.
    handleSelect =
        (index: number) =>
        (selectedRowIndex: number = index) => {
            this.setState({selectedRowIndex});
        };

    hoverAdder = () => {
        this.setState({isAdderHovered: true});
    };

    unhoverAdder = () => {
        this.setState({isAdderHovered: false});
    };

    getDotTop = (): number => {
        // Just some extra safety.
        if (typeof this.state.selectedRowIndex !== 'number') {
            return -100;
        }
        const selectedCircle = this.circles[this.state.selectedRowIndex];

        if (selectedCircle) {
            if (selectedCircle.firstChild && selectedCircle.firstChild instanceof HTMLElement) {
                return selectedCircle.firstChild.offsetTop;
            } else {
                return selectedCircle.offsetTop;
            }
        }

        return -100;
    };

    // No idea why, but I can't type this as T[], flow just blows up.
    findRowIndex = (collection: any, selectedId?: string | null) => {
        if (selectedId === null || typeof selectedId === 'undefined') return null;

        const preSelectedRowIndex: number = collection.findIndex((row: T) => row.id === selectedId);

        return preSelectedRowIndex !== -1 ? preSelectedRowIndex : null;
    };
}

type BarProps = {
    height: number;
    paddingTop: number;
    paddingBottom: number;
};

const Bar = ({height, paddingTop, paddingBottom}: BarProps) => (
    <div className={styles.bar}>
        <svg viewBox={`0 0 16 ${height}`} xmlns='http://www.w3.org/2000/svg'>
            <line
                x1='8'
                y1={paddingTop}
                x2='8'
                y2={height - paddingBottom}
                stroke={colors.greyDk}
            />
        </svg>
    </div>
);

const SelectionDot = ({top}: {top: number}) => (
    <Motion style={{y: spring(top, {stiffness: 300, damping: 28})}}>
        {(interpolated) => (
            <div style={{top: interpolated.y}} className={styles['selection-dot']}>
                <svg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'>
                    <circle cx='8' cy='8' r='2.5' fill={colors.blue} />
                </svg>
            </div>
        )}
    </Motion>
);
